import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BAR_CHART_OPTION,
  GROUPED_BAR_CHART_OPTION,
  INIT_CHART_OPTION_SMALL,
  INIT_KPI_CHART_OPTION_SMALL,
  KPI_KEYS_TO_CHART_TYPES_MAP,
  PIE_CHART_COLOR_MAPPING,
  PIE_CHART_OPTION
} from '@catalogues';
import {
  ChartSize,
  HistoricalKpi,
  Kpi,
  KpiCategoricalChartData,
  KpiChartConfig,
  KpiChartData,
  KpiChartDataConfig,
  KpiChartDataResponse,
  KpiChartId,
  KpiConfig,
  KpiKeys,
  KpisByCategory,
  KpiToggleChartData,
  MilestoneStage
} from '@models';
import {
  ChartService,
  CommonFunctionsService,
  ExpandedPanelStateService,
  KpiChartCalculationService,
  KpiService,
  SelectedKpiService
} from '@services';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import { KpiChartFilterService } from 'app/services/kpi-chart-filter/kpi-chart-filter.service';
import { KpiCommonFunctionsService } from 'app/services/kpi-common-functions/kpi-common-functions.service';
import { EChartsOption } from 'echarts';
import { featureFlags } from 'environments/environment';
import {
  catchError,
  distinctUntilChanged,
  forkJoin,
  iif,
  map,
  Observable,
  of,
  startWith,
  withLatestFrom
} from 'rxjs';

@Component({
  selector: 'kpi-charts-side-panel',
  templateUrl: './kpi-charts-side-panel.component.html',
  styleUrl: './kpi-charts-side-panel.component.scss'
})
export class KpiChartsSidePanelComponent implements OnChanges, OnInit {
  private kpiService: KpiService = inject(KpiService);
  private kpiChartCalculationService: KpiChartCalculationService = inject(
    KpiChartCalculationService
  );
  private kpiChartFilterService: KpiChartFilterService = inject(KpiChartFilterService);
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  private expandedPanelStateService: ExpandedPanelStateService = inject(ExpandedPanelStateService);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private selectedKpiService: SelectedKpiService = inject(SelectedKpiService);
  private kpiChartFacade: KpiChartFacadeService = inject(KpiChartFacadeService);
  private chartService: ChartService = inject(ChartService);

  private destroyRef: DestroyRef = inject(DestroyRef);

  private KPIS_WITH_CHART_DATA: KpiKeys[] = (
    [
      'active',
      'enrolled',
      'enrollment',
      'subject_screen_fail_rate',
      'earlyterm',
      'deviation_rate_per_subject',
      'clinical_material_kits_shipped',
      'crf_review_completed',
      'on_time_dispatch_clinical_materials',
      'source_data_verification',
      'days_to_close_site_findings',
      'days_to_pay_sites',
      'lab_sample_query_rate',
      'ereq_usage_for_lab_samples',
      'lab_kits_shipped',
      'lab_kit_wastage'
    ] as KpiKeys[]
  ).filter((key) => !featureFlags.hiddenDrilldownCharts.includes(key));

  @Input() kpisByCategory!: KpisByCategory;
  @Input() selectedKpiByKey?: { key: KpiKeys; kpi: Kpi };
  @Input() studySk!: string;
  @Input() protocolNumber!: string;
  @Input() milestoneStages!: MilestoneStage[];
  @Input() kpiConfig!: KpiConfig;

  chartsAreLoading$!: Observable<boolean>;
  chartSize: ChartSize = 'small';
  kpiDisplayValue?: string | number;
  kpiDisplaySecondaryValue?: string;
  kpiDisplayUnit?: string;

  openExpandedPanel = new EventEmitter<boolean>();
  expandedPanelExpanded!: boolean;
  kpiChartData!: Partial<Record<KpiKeys, KpiChartData>>;
  kpiChartsConfig!: KpiChartConfig[];
  hasCustomHeader = true;

  bar: EChartsOption = BAR_CHART_OPTION;
  pie: EChartsOption = PIE_CHART_OPTION;
  groupedBar: EChartsOption = GROUPED_BAR_CHART_OPTION;
  initKpiOption: EChartsOption = INIT_KPI_CHART_OPTION_SMALL;
  colorMap = PIE_CHART_COLOR_MAPPING;

  // Subscription to filter changes
  private activeKpiKey?: KpiKeys;
  kpiChartConfigs: Partial<Record<KpiKeys, KpiChartDataConfig[]>> = {};

  small: typeof INIT_CHART_OPTION_SMALL | any = INIT_CHART_OPTION_SMALL;
  browserTimezone = this.commonFunctionsService.getBrowserTimezone();

  public ngOnInit(): void {
    this.kpiChartData = this.kpiChartCalculationService.initializeDefaultKpiChartData(
      this.kpiConfig,
      this.kpisByCategory,
      this.milestoneStages
    );

    this.kpiChartFacade.syncChartData(this.kpiChartData);

    // Set up reactive loading state using facade service
    this.chartsAreLoading$ = this.kpiChartFacade.isActiveKpiLoading$.pipe(
      takeUntilDestroyed(this.destroyRef),
      withLatestFrom(this.kpiChartFacade.activeKpiChartData$.pipe(startWith(null))),
      map(([isLoading, chartData]) => {
        if (isLoading) return true;

        return !this.kpiChartFacade.hasValidChartData(chartData);
      }),
      distinctUntilChanged()
    );

    this.expandedPanelStateService.expandedPanelExpanded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((expanded) => {
        this.expandedPanelExpanded = expanded;
        this.kpiChartFilterService.clearAllFilters();
      });

    this.kpiChartFilterService.filterEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event && this.activeKpiKey) {
          this.updateChartsWithFilteredData(this.activeKpiKey);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const kpiChanged = changes['selectedKpiByKey'] !== undefined;

    if (kpiChanged) {
      this.onKpiSelected();
    }
  }

  expandedPanelCloseButtonClicked(): void {
    this.selectedKpiService.setSelectedKpi(undefined);
  }

  onOpenExpandedPanel() {
    this.expandedPanelStateService.setExpandedPanelExpanded(true);
  }

  getBarChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.findChartData<KpiCategoricalChartData>('bar', chartId);
  }

  getPieChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.findChartData<KpiCategoricalChartData>('pie', chartId);
  }

  getToggleChartData(chartId: KpiChartId): KpiToggleChartData | undefined {
    return this.findChartData<KpiToggleChartData>('toggleChart', chartId);
  }

  getGroupedBarChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.findChartData<KpiCategoricalChartData>('groupedBar', chartId);
  }

  private findChartData<T>(
    chartType: 'bar' | 'pie' | 'toggleChart' | 'groupedBar',
    chartId: KpiChartId
  ): T | undefined {
    if (!this.selectedKpiByKey) return;

    const currentChartData = this.kpiChartData[this.selectedKpiByKey.key];

    return currentChartData?.charts[chartType]?.find(
      (chartData) => chartData.identifier === chartId
    ) as T | undefined;
  }

  private onKpiSelected() {
    if (!this.selectedKpiByKey) return;

    const { key, kpi } = this.selectedKpiByKey;

    this.activeKpiKey = key;

    this.kpiChartsConfig = KPI_KEYS_TO_CHART_TYPES_MAP[key] || [];
    this.setKpiHeaderValues(kpi, key);

    this.kpiChartFacade.syncActiveKpi(key);
    this.kpiChartFacade.syncLoadingState(key, true);

    const currentKpiChartData = this.kpiChartData[key];

    if (!currentKpiChartData) return;

    if (currentKpiChartData.dataLoaded) {
      this.kpiChartFacade.syncLoadingState(key, false);
      this.kpiChartFilterService.initializeFilters(
        currentKpiChartData.chartSeedData?.unfiltered || []
      );
      return;
    }

    this.loadChartData(key);
  }

  private loadChartData(kpiKey: KpiKeys) {
    const historical$ = this.kpiService
      .getHistoricalKpi(this.studySk, kpiKey, this.browserTimezone)
      .pipe(
        catchError((err) => {
          console.error('Error fetching historical KPI:', err);
          return of(null);
        })
      );

    const chartData$ = iif(
      () => this.KPIS_WITH_CHART_DATA.includes(kpiKey),
      this.kpiService.getKpiChartData(this.studySk, kpiKey).pipe(
        catchError((err) => {
          console.error('Error fetching KPI chart data:', err);
          return of(null);
        })
      ),
      of(null)
    );

    const timeCycleData$ = iif(
      () => kpiKey === 'key_milestones_on_track_to_baseline',
      this.chartService.getTimeCycleData(this.studySk).pipe(
        catchError((err) => {
          console.error('Error fetching time cycle data:', err);
          return of(null);
        })
      ),
      of(null)
    );

    forkJoin([historical$, chartData$, timeCycleData$]).subscribe({
      next: ([historicalResponse, chartDataResponse, timeCycleDataResponse]) => {
        if (historicalResponse && historicalResponse.data.length > 0) {
          this.setKpiTrendChartDataFromHistoricalData(
            kpiKey,
            historicalResponse.data,
            historicalResponse.tooltipText
          );
        }
        if (chartDataResponse && chartDataResponse.data.length > 0) {
          this.kpiChartCalculationService.setKpiChartData(
            kpiKey,
            chartDataResponse,
            this.kpiChartData[kpiKey]!
          );
          this.kpiChartConfigs[kpiKey] = chartDataResponse.config;
          this.kpiChartFilterService.initializeFilters(chartDataResponse.data);
        }

        if (timeCycleDataResponse && timeCycleDataResponse.cycleCount > 0) {
          this.kpiChartCalculationService.setTimeCycleData(
            this.kpiChartData[kpiKey]!,
            timeCycleDataResponse
          );
        }
      },

      complete: () => {
        this.kpiChartData[kpiKey]!.dataLoaded = true;

        this.kpiChartFacade.syncChartData(this.kpiChartData);
        this.kpiChartFacade.syncLoadingState(kpiKey, false);

        // TOGGLE STATE INITIALIZATION: Set up default toggle states when data is first loaded
        if (this.kpiChartData[kpiKey]) {
          this.kpiChartFacade.initializeToggleStatesForKpi(kpiKey, this.kpiChartData[kpiKey]);
        }
      }
    });
  }

  private setKpiTrendChartDataFromHistoricalData(
    kpiKey: KpiKeys,
    historicalData: HistoricalKpi[],
    tooltipText?: string
  ) {
    if (!this.kpiChartData[kpiKey]) return;

    this.kpiChartCalculationService.setTrendChartData(
      this.kpiChartData[kpiKey].charts.trendchart,
      historicalData,
      kpiKey,
      tooltipText
    );
  }

  private setKpiHeaderValues(kpi: Kpi, kpiKey: KpiKeys): void {
    this.kpiDisplayValue = this.kpiCommonFunctionsService.getKpiDisplayValue(kpi, kpiKey);
    this.kpiDisplayUnit = this.kpiCommonFunctionsService.getKpiDisplayUnit(kpi.type);
    this.kpiDisplaySecondaryValue = this.kpiCommonFunctionsService.getKpiDisplaySecondaryValue(
      kpi,
      kpiKey
    );

    this.kpiChartFacade.syncDisplayValues({
      kpiDisplayValue: this.kpiDisplayValue,
      kpiDisplayUnit: this.kpiDisplayUnit,
      kpiDisplaySecondaryValue: this.kpiDisplaySecondaryValue
    });
  }

  private updateChartsWithFilteredData(kpiKey: KpiKeys): void {
    const filterState = this.kpiChartFilterService.getCurrentState();
    const kpiCharts = this.kpiChartData[kpiKey];

    if (!kpiCharts || !kpiCharts.chartSeedData || !kpiCharts.chartSeedData.unfiltered) {
      return;
    }

    try {
      this.kpiChartFacade.syncLoadingState(kpiKey, true);

      const hasActiveFilters = filterState.totalFilterCount > 0;
      const sourceData = hasActiveFilters
        ? filterState.filteredData
        : kpiCharts.chartSeedData.unfiltered;
      kpiCharts.chartSeedData.filtered = hasActiveFilters ? filterState.filteredData : undefined;

      const chartDataResponse = this.createChartDataResponse(sourceData, kpiKey);

      this.kpiChartCalculationService.setKpiChartData(kpiKey, chartDataResponse, kpiCharts);

      this.kpiChartFacade.syncChartData(this.kpiChartData);

      if (this.kpiChartData[kpiKey]) {
        this.kpiChartFacade.initializeToggleStatesForKpi(kpiKey, this.kpiChartData[kpiKey]);
      }
    } finally {
      this.kpiChartFacade.syncLoadingState(kpiKey, false);
    }
  }

  private createChartDataResponse(data: any[], kpiKey: KpiKeys): KpiChartDataResponse {
    return {
      data: data,
      config: this.kpiChartConfigs[kpiKey] || []
    };
  }
}
